import { endOfDay, startOfDay } from 'date-fns';
import { formatDateOnlyLocal, parseDateOnlyLocal } from '@erp/shared-utils';
import type { DateRange } from 'react-day-picker';

/**
 * Rebuild a date-only picker range from YYYY-MM-DD URL params using local
 * midnight. Do not use `parseISO` here — `"YYYY-MM-DD"` is UTC midnight and
 * shifts a day earlier in US timezones.
 */
export function hydrateDateOnlyRange(
  from?: string,
  to?: string,
): DateRange | undefined {
  if (!from && !to) return undefined;
  return {
    from: from ? (parseDateOnlyLocal(from) ?? undefined) : undefined,
    to: to ? (parseDateOnlyLocal(to) ?? undefined) : undefined,
  };
}

export function dateRangeToDateOnlyParams(range: DateRange | undefined): {
  from?: string;
  to?: string;
} {
  return {
    from: range?.from ? formatDateOnlyLocal(range.from) : undefined,
    to: range?.to ? formatDateOnlyLocal(range.to) : undefined,
  };
}

function toReceivedAtBoundIso(
  dateOnly: string | undefined,
  bound: 'start' | 'end',
): string | undefined {
  if (!dateOnly) return undefined;
  const date = parseDateOnlyLocal(dateOnly);
  if (!date) return undefined;
  return (bound === 'start' ? startOfDay(date) : endOfDay(date)).toISOString();
}

/**
 * Convert user-calendar received dates to inclusive UTC instants.
 * From is local start of day; to is local end of day (23:59:59.999).
 */
export function toReceivedAtIsoBounds(params: { from?: string; to?: string }): {
  receivedAtFrom?: string;
  receivedAtTo?: string;
} {
  return {
    receivedAtFrom: toReceivedAtBoundIso(params.from, 'start'),
    receivedAtTo: toReceivedAtBoundIso(params.to, 'end'),
  };
}
